import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import {
  EntityBadge,
  EntityBadgeById,
  type EntityBadgeState,
} from '@/lib/crm/components/badges';
import { SLOT_ID_RE } from '@/lib/email/meetingSlotTag';
import { logger } from '@/lib/logger';
import { MeetingSlotBadge } from '@/lib/markdown/MeetingSlotBadge';

import type { TableRefs } from './TableScrollContext';
import { useTableScroll } from './TableScrollContext';

// Copyable Table wrapper component
const CopyableTable: React.FC<{ children: React.ReactNode }> = React.memo(
  ({ children }) => {
    const id = useId();
    const [copied, setCopied] = useState(false);
    const placeholderRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLDivElement>(null);
    const [tableHeight, setTableHeight] = useState<number>(100);
    const [layout, setLayout] = useState<{
      width: number;
      left: number;
      scrollOffset: number;
    } | null>(null);

    const { registerTable, unregisterTable } = useTableScroll();

    // Find the content area helper
    const findContentArea = useCallback(() => {
      let el: HTMLElement | null = placeholderRef.current;
      while (el) {
        const style = getComputedStyle(el);
        if (style.position === 'relative' && style.overflowX === 'hidden') {
          return el;
        }
        el = el.parentElement;
      }
      return null;
    }, []);

    // Register table with context for scroll-synced positioning
    useEffect(() => {
      const refs: TableRefs = { placeholderRef, tableRef };
      registerTable(id, refs);
      return () => {
        unregisterTable(id);
      };
    }, [id, registerTable, unregisterTable]);

    // Step 1: Measure table height first (before positioning)
    // Use a ref to track measured height to avoid state update cycles
    const measuredHeightRef = useRef<number>(100);

    useEffect(() => {
      if (!tableRef.current) {
        return;
      }

      const measureHeight = () => {
        if (tableRef.current) {
          const height = tableRef.current.offsetHeight;
          if (height > 0) {
            // Store in ref immediately for comparison
            measuredHeightRef.current = height;
            // Only trigger state update if significantly different (> 1px)
            // This prevents micro-fluctuations from causing re-renders
            setTableHeight(prev =>
              Math.abs(prev - height) > 1 ? height : prev
            );
          }
        }
      };

      // Measure immediately and after delays to catch render
      measureHeight();
      const timer1 = setTimeout(measureHeight, 50);
      const timer2 = setTimeout(measureHeight, 150);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }, [children, layout]); // Re-measure when table renders

    // Step 2: Calculate layout (width, left, scrollOffset) - position handled by context
    useEffect(() => {
      if (!placeholderRef.current) {
        return;
      }

      const measureLayout = () => {
        const contentArea = findContentArea();
        const placeholder = placeholderRef.current;

        if (contentArea && placeholder) {
          const contentAreaRect = contentArea.getBoundingClientRect();
          const placeholderRect = placeholder.getBoundingClientRect();

          const left = 0;
          const width = contentArea.clientWidth;

          // Calculate scroll offset for alignment
          const scrollOffset = placeholderRect.left - contentAreaRect.left;

          setLayout({ width, left, scrollOffset });
        }
      };

      // Use requestAnimationFrame to ensure we measure after layout
      const rafId = requestAnimationFrame(() => {
        measureLayout();
      });

      // Also measure after a delay and on resize
      const timer = setTimeout(measureLayout, 100);

      const contentArea = findContentArea();
      if (contentArea) {
        const resizeObserver = new ResizeObserver(() => {
          requestAnimationFrame(measureLayout);
        });
        resizeObserver.observe(contentArea);
        return () => {
          resizeObserver.disconnect();
          cancelAnimationFrame(rafId);
          clearTimeout(timer);
        };
      }
      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(timer);
      };
    }, [children, tableHeight, findContentArea]);

    // Note: Transform positioning is handled by TableScrollContext via registerTable.
    // The context sets the initial position and updates on scroll/resize/mutations.

    const handleCopy = async () => {
      if (!tableRef.current) {
        return;
      }

      const table = tableRef.current.querySelector('table');
      if (!table) {
        return;
      }

      // Extract table data as tab-separated values
      const rows: string[] = [];
      table.querySelectorAll('tr').forEach(tr => {
        const cells: string[] = [];
        tr.querySelectorAll('th, td').forEach(cell => {
          cells.push((cell.textContent || '').trim());
        });
        rows.push(cells.join('\t'));
      });

      const tsv = rows.join('\n');

      try {
        await navigator.clipboard.writeText(tsv);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy table contents', error);
      }
    };

    // Render a placeholder in the flow + absolutely positioned table
    // The absolute positioning escapes the max-w-3xl overflow-x-clip
    return (
      <>
        {/* Placeholder maintains spacing in the document flow */}
        <div
          ref={placeholderRef}
          className='my-4 first:mt-0'
          style={{ height: tableHeight }}
        />
        {/* Absolutely positioned table - escapes overflow-x-clip */}
        {layout && (
          <div
            ref={tableRef}
            className='absolute'
            style={{
              top: 0,
              left: layout.left,
              width: layout.width,
              willChange: 'transform', // GPU layer for smooth transforms
            }}
          >
            <div
              className='rounded-xl border border-border-secondary shadow-xs overflow-hidden bg-bg-primary relative group'
              style={{
                marginLeft: layout.scrollOffset,
                marginRight: layout.scrollOffset,
              }}
            >
              {/* Copy button - positioned at top right of table */}
              <Button
                color='tertiary'
                onClick={handleCopy}
                title='Copy table to clipboard'
                className='absolute top-2 right-2 z-10 !p-1.5 !rounded-md bg-bg-primary/90 border border-border-secondary shadow-sm opacity-0 group-hover:opacity-100 hover:bg-bg-secondary'
                iconLeading={
                  copied ? (
                    <svg
                      className='size-4 text-green-600'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M5 13l4 4L19 7'
                      />
                    </svg>
                  ) : (
                    <svg
                      className='size-4 text-tertiary'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012-2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
                      />
                    </svg>
                  )
                }
              />
              <div className='overflow-x-auto'>
                <table className='border-collapse min-w-full'>{children}</table>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
);

// Module-scope component: entity mention span renderer for markdown.
// Reads orgId via useParams() so it doesn't close over parent-component state.
export const EntityMentionSpan = ({
  'data-entity': isEntity,
  'data-meeting-slot': isMeetingSlot,
  type,
  id,
  children,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement> & {
  'data-entity'?: string;
  'data-meeting-slot'?: string;
  type?: string;
  id?: string;
}) => {
  const { orgId } = useParams<{ orgId?: string }>();

  // Strip the "user-content-" prefix that rehype-sanitize adds for DOM
  // clobbering protection.
  const cleanId = id?.startsWith('user-content-')
    ? id.slice('user-content-'.length)
    : id;
  const parsedOrgId = orgId !== undefined ? parseInt(orgId) : undefined;

  // Meeting-slot chip (<span data-meeting-slot="true" id="UUID">), emitted by
  // preprocessEntityTags for an IdCodec meeting_slot reference. Render the
  // resolved wall-clock time via MeetingSlotBadge, which self-resolves against
  // the scheduling API. A non-UUID id is not a resolvable slot reference and
  // falls through to plain-span rendering. Any author content next to the chip
  // is emitted by preprocessEntityTags as a SIBLING (not a child of this span),
  // so rendering only the badge here drops nothing.
  if (
    isMeetingSlot &&
    parsedOrgId !== undefined &&
    Number.isFinite(parsedOrgId) &&
    cleanId !== undefined &&
    SLOT_ID_RE.test(cleanId)
  ) {
    return (
      <MeetingSlotBadge slotId={cleanId.toLowerCase()} orgId={parsedOrgId} />
    );
  }

  // The slot chip passed SLOT_ID_RE upstream in preprocessEntityTags but the
  // guard above failed anyway — in practice this means parsedOrgId is
  // missing/invalid (e.g. a route with no :orgId segment). Render the same
  // gray "resolve failure" pill MeetingSlotBadge shows on a lookup error,
  // rather than dropping the reference entirely.
  if (isMeetingSlot) {
    logger.warn('Unresolved meeting-slot reference: missing/invalid orgId', {
      orgId,
      cleanId,
    });
    return (
      <span className='inline-flex align-middle'>
        <Badge type='pill-color' color='gray'>
          Meeting time
        </Badge>
      </span>
    );
  }

  if (!isEntity) {
    return <span {...rest}>{children}</span>;
  }

  // Extract plain text from children. remark-gfm auto-links
  // emails/URLs inside the entity tag, so children may arrive
  // as a React element (e.g. <a>) rather than a plain string.
  // Recursively unwrap via props.children to get the text.
  const extractText = (node: unknown): string => {
    if (typeof node === 'string') {
      return node;
    }
    if (typeof node === 'number') {
      return String(node);
    }
    if (Array.isArray(node)) {
      return node.map(extractText).join('');
    }
    if (typeof node === 'object' && node !== null && 'props' in node) {
      const reactNode = node as { props?: { children?: unknown } };
      if (reactNode.props?.children != null) {
        return extractText(reactNode.props.children);
      }
    }
    return '';
  };
  const name = extractText(children) || 'Unknown';
  if (
    parsedOrgId !== undefined &&
    Number.isFinite(parsedOrgId) &&
    cleanId !== undefined
  ) {
    return (
      <EntityBadgeById
        entityId={cleanId}
        orgId={parsedOrgId}
        variant='inline'
      />
    );
  }
  return (
    <MarkdownEntityFallbackBadge
      entityId={cleanId ?? ''}
      entityType={type || ''}
      displayName={name}
    />
  );
};

// Static fallback badge used when the markdown entity tag lacks the
// org/id needed for live store hydration. Owns the `useMemo` so the
// state literal stays referentially stable across re-renders.
const MarkdownEntityFallbackBadge = ({
  entityId,
  entityType,
  displayName,
}: {
  entityId: string;
  entityType: string;
  displayName: string;
}) => {
  const state = React.useMemo<EntityBadgeState>(
    () => ({
      kind: 'data',
      data: { id: entityId, entityType, displayName },
    }),
    [entityId, entityType, displayName]
  );
  return (
    <EntityBadge
      state={state}
      variant='inline'
      interactive={false}
      className='-translate-y-px'
    />
  );
};

// Module-scope component: table renderer that wraps in CopyableTable.
export const MarkdownCopyableTable = ({
  children,
}: React.HTMLAttributes<HTMLTableElement>) => (
  <CopyableTable>{children}</CopyableTable>
);
